import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import {
  Quote,
  CreateQuoteData,
  UpdateQuoteData,
  QuoteTag,
  QuoteComment,
  QuoteType,
  UpstreamMaterialPrice,
} from '../types/quote.types';

/**
 * Servicio para cotizaciones
 */
class QuoteService {
  async getQuotes(): Promise<Quote[]> {
    return apiClient.get<Quote[]>(ENDPOINTS.QUOTES);
  }

  async getQuote(id: string): Promise<Quote> {
    return apiClient.get<Quote>(`${ENDPOINTS.QUOTES}/${id}`);
  }

  async createQuote(data: CreateQuoteData): Promise<Quote> {
    return apiClient.post<Quote>(ENDPOINTS.QUOTES, data);
  }

  async saveDraft(data: Partial<CreateQuoteData>): Promise<Quote> {
    return apiClient.post<Quote>(`${ENDPOINTS.QUOTES}/draft`, data);
  }

  async updateDraft(id: string, data: Partial<CreateQuoteData>): Promise<Quote> {
    return apiClient.put<Quote>(`${ENDPOINTS.QUOTES}/${id}/draft`, data);
  }

  async updateQuote(id: string, data: UpdateQuoteData): Promise<Quote> {
    return apiClient.put<Quote>(`${ENDPOINTS.QUOTES}/${id}`, data);
  }

  async deleteQuote(id: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.QUOTES}/${id}`);
  }

  async submitQuote(id: string): Promise<Quote> {
    return apiClient.post<Quote>(`${ENDPOINTS.QUOTES}/${id}/submit`);
  }

  // ── Share token (portal cliente) ────────────────────────────────

  async shareQuote(
    id: string,
    expiresInDays?: number
  ): Promise<{ token: string; expiresAt: string | null }> {
    return apiClient.post<{ token: string; expiresAt: string | null }>(
      `${ENDPOINTS.QUOTES}/${id}/share`,
      { expiresInDays }
    );
  }

  async revokeQuoteShare(id: string): Promise<void> {
    return apiClient.delete<void>(`${ENDPOINTS.QUOTES}/${id}/share`);
  }

  async calculateQuote(data: Partial<CreateQuoteData>): Promise<any> {
    return apiClient.post<any>(`${ENDPOINTS.QUOTES}/calculate`, data);
  }

  // Templates
  async getTemplates(): Promise<Quote[]> {
    return apiClient.get<Quote[]>(ENDPOINTS.QUOTE_TEMPLATES);
  }

  async saveAsTemplate(id: string, templateName: string): Promise<Quote> {
    return apiClient.post<Quote>(ENDPOINTS.QUOTE_SAVE_AS_TEMPLATE(id), { templateName });
  }

  async createFromTemplate(templateId: string): Promise<Quote> {
    return apiClient.post<Quote>(ENDPOINTS.QUOTE_FROM_TEMPLATE(templateId));
  }

  // Tags
  async getTags(): Promise<QuoteTag[]> {
    return apiClient.get<QuoteTag[]>(ENDPOINTS.QUOTE_TAGS);
  }

  async createTag(name: string, color: string): Promise<QuoteTag> {
    return apiClient.post<QuoteTag>(ENDPOINTS.QUOTE_TAGS, { name, color });
  }

  async deleteTag(id: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.QUOTE_TAG_BY_ID(id));
  }

  async setQuoteTags(quoteId: string, tagIds: string[]): Promise<QuoteTag[]> {
    return apiClient.post<QuoteTag[]>(ENDPOINTS.QUOTE_ATTACH_TAGS(quoteId), { tagIds });
  }

  async removeQuoteTag(quoteId: string, tagId: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.QUOTE_DETACH_TAG(quoteId, tagId));
  }

  // Comments
  async getComments(quoteId: string): Promise<QuoteComment[]> {
    return apiClient.get<QuoteComment[]>(ENDPOINTS.QUOTE_COMMENTS(quoteId));
  }

  async createComment(quoteId: string, content: string): Promise<QuoteComment> {
    return apiClient.post<QuoteComment>(ENDPOINTS.QUOTE_COMMENTS(quoteId), { content });
  }

  async deleteComment(quoteId: string, commentId: string): Promise<void> {
    return apiClient.delete<void>(ENDPOINTS.QUOTE_COMMENT_BY_ID(quoteId, commentId));
  }

  // Quote Types
  async getQuoteTypes(): Promise<QuoteType[]> {
    return apiClient.get<QuoteType[]>(ENDPOINTS.QUOTE_TYPES);
  }

  async resolveUpstreamPrices(
    quoteTypeId: string,
    materialIds: string[],
    sourceQuoteId?: string,
    targetCurrencyCode?: string,
    referenceDate?: string
  ): Promise<Record<string, UpstreamMaterialPrice>> {
    return apiClient.post<Record<string, UpstreamMaterialPrice>>(
      ENDPOINTS.QUOTE_TYPE_RESOLVE_PRICES,
      { quoteTypeId, materialIds, sourceQuoteId, targetCurrencyCode, referenceDate }
    );
  }

  async getApprovedUpstreamQuotes(quoteTypeId: string): Promise<
    Array<{
      id: string;
      code: string;
      clientName: string;
      updatedAt: string;
      lineItemCount: number;
    }>
  > {
    return apiClient.get<any[]>(ENDPOINTS.QUOTE_TYPE_APPROVED_QUOTES(quoteTypeId));
  }

  async getQuoteTypeMaterialPrices(quoteTypeId: string): Promise<any[]> {
    return apiClient.get<any[]>(ENDPOINTS.QUOTE_TYPE_MATERIAL_PRICES(quoteTypeId));
  }

  async getSourceQuoteLineItems(sourceQuoteId: string): Promise<{
    sourceQuoteId: string;
    sourceQuoteCode: string;
    sections: Array<{
      sectionId: string;
      sectionLabel: string;
      items: Array<{
        materialId: string;
        quantity: number;
        unitPrice: number;
        totalPrice: number;
      }>;
    }>;
    legacyItems: Array<{
      materialId: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
    }>;
    allItems: Array<{
      materialId: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
    }>;
  }> {
    return apiClient.get(`/api/quote-types/source-quotes/${sourceQuoteId}/line-items`);
  }
}

export const quoteService = new QuoteService();
export default quoteService;
